import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { dialogPortaledPopoverGuard } from '@/components/ui/dialog-portaled-popover-guard'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useMachineryDirectiveEhsrList } from '../../hooks/useMachineryDirectiveEhsrList'
import { EhsrSelect } from '../hazards/EhsrSelect'
import {
  defaultRiskReductionMeasureListFilters,
  type RiskReductionMeasureListFilters,
} from './riskReductionMeasureListFilters'

const MEASURE_TYPES = ['ISD', 'SCP', 'PPE', 'OI'] as const
const PLR_LEVELS = ['A', 'B', 'C', 'D', 'E'] as const

type RiskReductionMeasureFiltersSheetProps = {
  filters: RiskReductionMeasureListFilters
  onFiltersChange: (filters: RiskReductionMeasureListFilters) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RiskReductionMeasureFiltersSheet({
  filters,
  onFiltersChange,
  open,
  onOpenChange,
}: RiskReductionMeasureFiltersSheetProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const ehsrOptions = useMachineryDirectiveEhsrList()
  const [draftFilters, setDraftFilters] =
    useState<RiskReductionMeasureListFilters>(filters)

  useEffect(() => {
    if (open) {
      setDraftFilters(filters)
    }
  }, [open, filters])

  const handleApply = useCallback(() => {
    onFiltersChange(draftFilters)
    onOpenChange(false)
  }, [draftFilters, onFiltersChange, onOpenChange])

  const handleClearAll = useCallback(() => {
    setDraftFilters(defaultRiskReductionMeasureListFilters)
  }, [])

  const allLabel = t('riskReductionMeasuresLibraryPage.filters.all')

  const measureTypeItems = useMemo(
    () => [
      { value: 'all', label: allLabel },
      ...MEASURE_TYPES.map((value) => ({
        value,
        label: t(
          `riskReductionMeasuresLibraryPage.form.measureTypes.${value}`
        ),
      })),
    ],
    [allLabel, t]
  )

  const sourceItems = useMemo(
    () => [
      { value: 'all', label: allLabel },
      {
        value: 'USER',
        label: t('riskReductionMeasuresLibraryPage.source.user'),
      },
      {
        value: 'STANDARD',
        label: t('riskReductionMeasuresLibraryPage.source.standard'),
      },
    ],
    [allLabel, t]
  )

  const dependsOnControlSystemItems = useMemo(
    () => [
      { value: 'all', label: allLabel },
      { value: 'yes', label: t('riskReductionMeasuresLibraryPage.form.yes') },
      { value: 'no', label: t('riskReductionMeasuresLibraryPage.form.no') },
    ],
    [allLabel, t]
  )

  const safetyFunctionItems = useMemo(
    () => [
      { value: 'all', label: allLabel },
      {
        value: 'assigned',
        label: t('riskReductionMeasuresLibraryPage.filters.safetyFunctionAssigned'),
      },
      {
        value: 'not_assigned',
        label: t(
          'riskReductionMeasuresLibraryPage.filters.safetyFunctionNotAssigned'
        ),
      },
    ],
    [allLabel, t]
  )

  const plrItems = useMemo(
    () => [
      { value: 'all', label: allLabel },
      ...PLR_LEVELS.map((value) => ({ value, label: value })),
      {
        value: 'none',
        label: t('riskReductionMeasuresLibraryPage.filters.plrNone'),
      },
    ],
    [allLabel, t]
  )

  const linkedHazardsItems = useMemo(
    () => [
      { value: 'all', label: allLabel },
      {
        value: 'linked',
        label: t('riskReductionMeasuresLibraryPage.filters.linkedToHazardsLinked'),
      },
      {
        value: 'not_linked',
        label: t(
          'riskReductionMeasuresLibraryPage.filters.linkedToHazardsNotLinked'
        ),
      },
    ],
    [allLabel, t]
  )

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='flex w-full flex-col sm:max-w-md'
        {...dialogPortaledPopoverGuard}
      >
        <SheetHeader>
          <SheetTitle>
            {t('riskReductionMeasuresLibraryPage.filters.title')}
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className='min-h-0 flex-1 px-4'>
          <div className='space-y-6 pb-4'>
            <div className='space-y-2'>
              <Label id='rrm-filter-measure-type-label'>
                {t('riskReductionMeasuresLibraryPage.filters.measureTypeLabel')}
              </Label>
              <Select
                value={draftFilters.measureType}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  setDraftFilters((previous) => ({
                    ...previous,
                    measureType: value as RiskReductionMeasureListFilters['measureType'],
                  }))
                }}
                items={measureTypeItems}
              >
                <SelectTrigger
                  className='w-full'
                  aria-labelledby='rrm-filter-measure-type-label'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{allLabel}</SelectItem>
                  {MEASURE_TYPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(
                        `riskReductionMeasuresLibraryPage.form.measureTypes.${value}`
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label id='rrm-filter-source-label'>
                {t('riskReductionMeasuresLibraryPage.filters.sourceLabel')}
              </Label>
              <Select
                value={draftFilters.source}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  setDraftFilters((previous) => ({
                    ...previous,
                    source: value as RiskReductionMeasureListFilters['source'],
                  }))
                }}
                items={sourceItems}
              >
                <SelectTrigger
                  className='w-full'
                  aria-labelledby='rrm-filter-source-label'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{allLabel}</SelectItem>
                  <SelectItem value='USER'>
                    {t('riskReductionMeasuresLibraryPage.source.user')}
                  </SelectItem>
                  <SelectItem value='STANDARD'>
                    {t('riskReductionMeasuresLibraryPage.source.standard')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label>{t('hazardsPage.hazardDetail.ehsr')}</Label>
              <EhsrSelect
                options={ehsrOptions}
                value={draftFilters.ehsrClauseIds}
                onChange={(ehsrClauseIds) =>
                  setDraftFilters((previous) => ({
                    ...previous,
                    ehsrClauseIds,
                  }))
                }
              />
              <div className='flex items-center gap-2'>
                <Checkbox
                  id='rrm-filter-no-ehsr'
                  checked={draftFilters.includeNoEhsr}
                  onCheckedChange={(checked) =>
                    setDraftFilters((previous) => ({
                      ...previous,
                      includeNoEhsr: checked === true,
                    }))
                  }
                />
                <Label
                  htmlFor='rrm-filter-no-ehsr'
                  className='cursor-pointer text-sm font-normal'
                >
                  {t('riskReductionMeasuresLibraryPage.filters.noEhsr')}
                </Label>
              </div>
            </div>

            <div className='space-y-2'>
              <Label id='rrm-filter-depends-on-control-system-label'>
                {t(
                  'riskReductionMeasuresLibraryPage.filters.dependsOnControlSystemLabel'
                )}
              </Label>
              <Select
                value={draftFilters.dependsOnControlSystem}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  setDraftFilters((previous) => ({
                    ...previous,
                    dependsOnControlSystem:
                      value as RiskReductionMeasureListFilters['dependsOnControlSystem'],
                  }))
                }}
                items={dependsOnControlSystemItems}
              >
                <SelectTrigger
                  className='w-full'
                  aria-labelledby='rrm-filter-depends-on-control-system-label'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{allLabel}</SelectItem>
                  <SelectItem value='yes'>
                    {t('riskReductionMeasuresLibraryPage.form.yes')}
                  </SelectItem>
                  <SelectItem value='no'>
                    {t('riskReductionMeasuresLibraryPage.form.no')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label id='rrm-filter-safety-function-label'>
                {t('riskReductionMeasuresLibraryPage.filters.safetyFunctionLabel')}
              </Label>
              <Select
                value={draftFilters.safetyFunction}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  setDraftFilters((previous) => ({
                    ...previous,
                    safetyFunction:
                      value as RiskReductionMeasureListFilters['safetyFunction'],
                  }))
                }}
                items={safetyFunctionItems}
              >
                <SelectTrigger
                  className='w-full'
                  aria-labelledby='rrm-filter-safety-function-label'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{allLabel}</SelectItem>
                  <SelectItem value='assigned'>
                    {t(
                      'riskReductionMeasuresLibraryPage.filters.safetyFunctionAssigned'
                    )}
                  </SelectItem>
                  <SelectItem value='not_assigned'>
                    {t(
                      'riskReductionMeasuresLibraryPage.filters.safetyFunctionNotAssigned'
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label id='rrm-filter-plr-label'>
                {t('riskReductionMeasuresLibraryPage.filters.plrLabel')}
              </Label>
              <Select
                value={draftFilters.plr}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  setDraftFilters((previous) => ({
                    ...previous,
                    plr: value as RiskReductionMeasureListFilters['plr'],
                  }))
                }}
                items={plrItems}
              >
                <SelectTrigger
                  className='w-full'
                  aria-labelledby='rrm-filter-plr-label'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{allLabel}</SelectItem>
                  {PLR_LEVELS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {value}
                    </SelectItem>
                  ))}
                  <SelectItem value='none'>
                    {t('riskReductionMeasuresLibraryPage.filters.plrNone')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label id='rrm-filter-linked-hazards-label'>
                {t('riskReductionMeasuresLibraryPage.filters.linkedToHazardsLabel')}
              </Label>
              <Select
                value={draftFilters.linkedToHazards}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  setDraftFilters((previous) => ({
                    ...previous,
                    linkedToHazards:
                      value as RiskReductionMeasureListFilters['linkedToHazards'],
                  }))
                }}
                items={linkedHazardsItems}
              >
                <SelectTrigger
                  className='w-full'
                  aria-labelledby='rrm-filter-linked-hazards-label'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{allLabel}</SelectItem>
                  <SelectItem value='linked'>
                    {t(
                      'riskReductionMeasuresLibraryPage.filters.linkedToHazardsLinked'
                    )}
                  </SelectItem>
                  <SelectItem value='not_linked'>
                    {t(
                      'riskReductionMeasuresLibraryPage.filters.linkedToHazardsNotLinked'
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className='flex-row gap-2 border-t pt-4'>
          <Button
            type='button'
            variant='outline'
            className='flex-1'
            onClick={handleClearAll}
          >
            {t('riskReductionMeasuresLibraryPage.filters.clearAll')}
          </Button>
          <Button type='button' className='flex-1' onClick={handleApply}>
            {t('riskReductionMeasuresLibraryPage.filters.apply')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
